import React, { Component } from "react";
import { motion } from "framer-motion";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";

import { primary } from "@formant/ui/component/common/Colors";
import { Icon, IconName } from "@formant/ui/component/common/Icon";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { Typography } from "@formant/ui/component/common/Typography";

import { Button, ButtonProps } from "./Button";
import { CircleButton } from "./CircleButton";
import { Menu } from "./Menu";
import { IMenuItem } from "./Menu/IMenuItem";

export interface IListButtonProps extends ButtonProps {
    icon?: IconName;
    label: string;
    kebab?: boolean;
    empty?: boolean;
    loading?: boolean;
    className?: string;
    onDelete: (item: string) => void;
}

@observer
class ListButtonComponent extends Component<IListButtonProps> {
    @observable
    private menuVisible: boolean = false;

    public render() {
        const { className } = this.props;
        const { empty, kebab, icon, label, onDelete, loading, ...buttonProps } =
            {
                ...this.props,
            };

        const menuItems: IMenuItem[] = [
            {
                icon: "delete",
                value: "Delete",
                onSelected: () => onDelete(label),
            },
        ];

        const menuButton = kebab && (
            <Kebab
                data-value="child"
                size="18"
                icon="kebab"
                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    event.stopPropagation();
                    this.menuVisible = true;
                }}
            />
        );

        const handleClick = (
            event: React.MouseEvent<HTMLElement, MouseEvent>,
        ) => {
            event.preventDefault();
            const dataValue = (event.target as HTMLElement).getAttribute(
                "data-testid",
            );

            if (dataValue !== "icon:kebab" && this.props?.onClick) {
                this.props?.onClick(event);
            }
        };

        return (
            <motion.div
                whileHover="hover"
                onTap={(event, _) => {
                    const e = event as unknown as {
                        offsetX?: number;
                        target?: {
                            localName?: string;
                            innerHTML?: string;
                        };
                    };

                    const target = e.target?.localName;
                    if (
                        (label === "Add..." ||
                            (e.offsetX || 0) > 13 ||
                            target === "svg") &&
                        !e?.target?.innerHTML?.includes("icon:kebab")
                    ) {
                        handleClick(
                            event as unknown as React.MouseEvent<
                                HTMLElement,
                                MouseEvent
                            >,
                        );
                    }
                }}
            >
                <StyledButton
                    className={className}
                    {...buttonProps}
                    onClick={() => {}}
                >
                    {kebab && (
                        <>
                            {menuButton}
                            <Menu
                                visible={this.menuVisible}
                                items={menuItems}
                                bottom={true}
                                onClose={() => {
                                    this.menuVisible = false;
                                }}
                            />
                        </>
                    )}
                    <StyledTypography
                        color={empty ? primary.silver : primary.white}
                        type="list"
                    >
                        {label}
                        {loading && <LoadingIndicator visible={true} />}
                    </StyledTypography>
                    {icon && (
                        <motion.span variants={{ hover: { x: "0.25rem" } }}>
                            <Icon name={icon} size="18" />
                        </motion.span>
                    )}
                </StyledButton>
            </motion.div>
        );
    }
}

const StyledButton = styled(Button)``;

export const ListButton = styled(ListButtonComponent)`
    position: relative;
    width: 100%;
    height: 2.5rem;
    border-radius: 0.1875rem;
    background-color: ${primary.steel02};
    display: flex;
    padding-left: 0.625rem;
    align-items: center;
    text-align: center;
    margin-bottom: 0.125rem;
    color: ${primary.white};

    ${(props: IListButtonProps) =>
        props.empty === true &&
        css`
            background-color: ${primary.steel01};
            margin-bottom: 0px;
            color: ${primary.silver};
            z-index: 0;
        `}

    svg {
        margin-right: 0.4875rem;
        fill: ${primary.white};
    }
`;

const Kebab = styled(CircleButton)`
    &&& {
        padding-top: 0rem;
        background-color: transparent;
        &:hover {
            background: ${primary.steel01};
        }

        svg {
            margin-top: 0.1875rem;
            margin-right: 0rem;
        }
    }
`;

const StyledTypography = styled(Typography)`
    width: 17.25rem;
    text-align: left;
    padding-left: 0.4875rem;
`;
